import { useEffect, useState } from 'react';

import { Button, Stack, TextField } from '@mui/material';

import * as DataListAPI from '../../../api/dataList';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import SystemTabs from '../../../ui/SystemTabs';
import Checkbox from '../../form/Checkbox';
import DataList from '../../form/DataList';
import SelectEmpresa from '../../form/SelectEmpresa';

function Form({
    dados,
    setDados,
    handleSubmit,
    caminhoVoltar,
    OptionsEmpresa,
    editando,
    carregando,
    salvando,
    falhaCarregamento,
}) {
    const navigate = useNavigateParams();
    const { setFlashMessage } = useFlashMessage();
    const [optionsUnidade, setOptionsUnidade] = useState([]);
    const [optionsPessoa, setOptionsPessoa] = useState([]);
    const [unidade, setUnidade] = useState(dados?.UnidadeCondominial || {});
    const [pessoa, setPessoa] = useState(dados?.Pessoa || {});

    useEffect(() => {
        setUnidade(dados?.UnidadeCondominial || {});
    }, [dados?.UnidadeCondominial]);

    useEffect(() => {
        setPessoa(dados?.Pessoa || {});
    }, [dados?.Pessoa]);

    async function consultarUnidades(filtro = '') {
        if (!dados?.empresaId) {
            setOptionsUnidade([]);
            return [];
        }

        try {
            const result = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'UnidadeCondominial',
                filtro,
            });

            const opcoes = Array.isArray(result) ? result : [];
            setOptionsUnidade(opcoes);
            return opcoes;
        } catch (error) {
            setOptionsUnidade([]);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as unidades condominiais.',
                error.response?.data?.type || 'error'
            );
            return [];
        }
    }

    async function consultarPessoas(filtro = '') {
        if (!dados?.empresaId) {
            setOptionsPessoa([]);
            return [];
        }

        try {
            const result = await DataListAPI.dataList({
                empresaId: dados.empresaId,
                tabela: 'Pessoa',
                filtro,
            });

            setOptionsPessoa(result);
            return result;
        } catch (error) {
            setOptionsPessoa([]);
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as pessoas.',
                error.response?.data?.type || 'error'
            );
            return [];
        }
    }

    async function handleChange(event) {
        const { name, value, type, checked } = event.target;

        if (name === 'empresaId') {
            setUnidade({});
            setPessoa({});
            setOptionsUnidade([]);
            setOptionsPessoa([]);
            setDados((anterior) => ({
                ...anterior,
                empresaId: value,
                unidadeCondominialId: null,
                pessoaId: null,
                UnidadeCondominial: {},
                Pessoa: {},
            }));
            return;
        }

        if (name === 'UnidadeCondominial.descricao') {
            const valorSelecionado = String(value || '').trim();
            const opcaoSelecionada = optionsUnidade.find((opcao) => String(opcao?.descricao || '').trim() === valorSelecionado);

            if (opcaoSelecionada) {
                setUnidade(opcaoSelecionada);
                setDados((anterior) => ({
                    ...anterior,
                    unidadeCondominialId: Number(opcaoSelecionada.id),
                    UnidadeCondominial: opcaoSelecionada,
                }));
                return;
            }

            setUnidade({ descricao: valorSelecionado });

            setDados((anterior) => ({
                ...anterior,
                unidadeCondominialId: null,
                UnidadeCondominial: { descricao: valorSelecionado },
            }));

            await consultarUnidades(valorSelecionado);
            return;
        }

        if (name === 'Pessoa.descricao') {
            const valorSelecionado = String(value || '').trim();
            const opcaoSelecionada = optionsPessoa.find((opcao) => String(opcao?.descricao || '').trim() === valorSelecionado);

            if (opcaoSelecionada) {
                setPessoa(opcaoSelecionada);
                setDados((anterior) => ({
                    ...anterior,
                    pessoaId: Number(opcaoSelecionada.id),
                    Pessoa: opcaoSelecionada,
                }));
                return;
            }

            setPessoa({ descricao: valorSelecionado });

            setDados((anterior) => ({
                ...anterior,
                pessoaId: null,
                Pessoa: { descricao: valorSelecionado },
            }));

            await consultarPessoas(valorSelecionado);
            return;
        }

        if (name === 'dataFim' && !value) {
            setDados((anterior) => ({ ...anterior, dataFim: '' }));
            return;
        }

        if (name === 'inquilino') {
            setDados((anterior) => ({
                ...anterior,
                inquilino: checked,
                morador: checked ? true : anterior.morador,
            }));
            return;
        }

        if (name === 'morador' && !checked) {
            setDados((anterior) => ({
                ...anterior,
                morador: false,
                inquilino: false,
            }));
            return;
        }

        setDados((anterior) => ({
            ...anterior,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }

    async function handleOnFocus(event) {
        if (editando) return;

        if (event.target.name === 'UnidadeCondominial.descricao') {
            await consultarUnidades(event.target.value);
        }

        if (event.target.name === 'Pessoa.descricao') {
            await consultarPessoas(event.target.value);
        }
    }

    function submit(event) {
        event.preventDefault();
        handleSubmit(dados);
    }

    const tabVinculo = (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <SelectEmpresa
                    name="empresaId"
                    text="Empresa"
                    options={OptionsEmpresa}
                    handleOnChange={handleChange}
                    value={dados?.empresaId || ''}
                    disabled={editando}
                    required
                    notOptNull={false}
                />

                <DataList
                    text="Unidade condominial"
                    name="UnidadeCondominial.descricao"
                    inputWidth="500px"
                    filteredOptions={optionsUnidade}
                    handleOnChange={handleChange}
                    handleOnFocus={handleOnFocus}
                    value={unidade?.descricao || ''}
                    readonly={editando || !dados?.empresaId}
                    required
                />
            </Stack>

            <DataList
                text="Pessoa"
                name="Pessoa.descricao"
                inputWidth="600px"
                filteredOptions={optionsPessoa}
                handleOnChange={handleChange}
                handleOnFocus={handleOnFocus}
                value={pessoa?.descricao || ''}
                readonly={editando || !dados?.empresaId}
                required
            />

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <Checkbox
                    title="Indica que a pessoa é proprietária da unidade"
                    text="Proprietário"
                    name="proprietario"
                    handleOnChange={handleChange}
                    value={dados?.proprietario}
                    checked={Boolean(dados?.proprietario)}
                />

                <Checkbox
                    title="Indica que a pessoa responde financeiramente pela unidade"
                    text="Responsável financeiro"
                    name="responsavelFinanceiro"
                    handleOnChange={handleChange}
                    value={dados?.responsavelFinanceiro}
                    checked={Boolean(dados?.responsavelFinanceiro)}
                />

                <Checkbox
                    title="Indica que a pessoa reside na unidade"
                    text="Morador"
                    name="morador"
                    handleOnChange={handleChange}
                    value={dados?.morador}
                    checked={Boolean(dados?.morador)}
                />

                <Checkbox
                    title="Indica que o morador ocupa a unidade na condição de inquilino"
                    text="Inquilino"
                    name="inquilino"
                    handleOnChange={handleChange}
                    value={dados?.inquilino}
                    checked={Boolean(dados?.inquilino)}
                />
            </Stack>

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TextField
                    name="dataInicio"
                    label="Início do vínculo"
                    type="date"
                    value={dados?.dataInicio || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                />

                <TextField
                    name="dataFim"
                    label="Término do vínculo"
                    type="date"
                    value={dados?.dataFim || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Stack>

            <Checkbox
                title="Indica que o vínculo pode ser utilizado nas rotinas do condomínio"
                text="Ativo"
                name="ativo"
                handleOnChange={handleChange}
                value={dados?.ativo}
                checked={Boolean(dados?.ativo)}
            />

            <TextField
                name="observacao"
                label="Observação"
                value={dados?.observacao || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
            />
        </Stack>
    );

    const tabsConfig = [
        {
            key: 'vinculo',
            label: 'Vínculo',
            title: 'Pessoa vinculada à unidade condominial',
            panel: tabVinculo,
        },
    ];

    return (
        <form onSubmit={submit} autoComplete="off">
            <h1>{editando ? 'EDIÇÃO DA PESSOA NA UNIDADE' : 'CADASTRO DA PESSOA NA UNIDADE'}</h1>

            <SystemTabs tabs={tabsConfig} initialKey="vinculo" />

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    type="button"
                    disabled={salvando}
                    onClick={() => navigate(caminhoVoltar)}
                >
                    Voltar
                </Button>

                <Button type="submit" variant="contained" color="success" disabled={carregando || salvando || falhaCarregamento}>
                    {salvando ? 'Salvando...' : editando ? 'Atualizar' : 'Cadastrar'}
                </Button>
            </Stack>
        </form>
    );
}

export default Form;
